import { useEffect, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Field } from '../../ui';
import { PasswordInput } from '../common/PasswordInput';
import { mfaApi } from '../../services/mfaApi';
import type { VerifyIdentityMethod } from '../../types/mfa';
import { MfaCodeInput } from './MfaCodeInput';
import './ConfirmIdentityModal.css';

interface ConfirmIdentityModalProps {
  open: boolean;
  // Whether the user has TOTP enabled — controls whether we offer the
  // authenticator option in the modal.
  totpAvailable?: boolean;
  // Title and description shown to the user; the calling action determines
  // the wording (e.g. "Confirm to delete organization").
  title?: string;
  description?: string;
  onConfirmed: () => void;
  onCancel: () => void;
}

/**
 * Step-up authentication modal. Used in front of sensitive actions: the
 * caller renders this when its mutation returns 403 step_up_required. After
 * a successful POST /api/auth/verify-identity we call onConfirmed and the
 * caller retries the original request.
 */
export function ConfirmIdentityModal({
  open,
  totpAvailable,
  title = 'Confirm your identity',
  description = 'For security, please confirm your identity to continue.',
  onConfirmed,
  onCancel,
}: ConfirmIdentityModalProps) {
  const [method, setMethod] = useState<VerifyIdentityMethod>(totpAvailable ? 'totp' : 'password');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when the modal opens fresh — avoids leaking values across calls.
  useEffect(() => {
    if (open) {
      setMethod(totpAvailable ? 'totp' : 'password');
      setPassword('');
      setCode('');
      setError(null);
    }
  }, [open, totpAvailable]);

  const verifyMutation = useMutation({
    mutationFn: () =>
      mfaApi.verifyIdentity(method === 'password' ? { method, password } : { method, code }),
    onSuccess: response => {
      if (response.success) {
        onConfirmed();
      } else {
        setError(response.error || 'Verification failed');
      }
    },
    onError: (err: Error) => setError(err.message || 'Verification failed'),
  });

  if (!open) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (method === 'password' && !password) {
      setError('Enter your password.');
      return;
    }
    if (method !== 'password' && code.length !== 6) {
      setError('Enter all 6 digits.');
      return;
    }
    verifyMutation.mutate();
  };

  return (
    <div className="confirm-identity-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="confirm-identity-modal__backdrop" onClick={onCancel} aria-hidden="true" />
      <div className="confirm-identity-modal__panel">
        <h2 className="confirm-identity-modal__title">{title}</h2>
        <p className="confirm-identity-modal__description">{description}</p>

        {totpAvailable && (
          <div className="confirm-identity-modal__methods" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={method === 'totp'}
              className={method === 'totp' ? 'is-active' : ''}
              onClick={() => setMethod('totp')}
            >
              Authenticator code
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={method === 'password'}
              className={method === 'password' ? 'is-active' : ''}
              onClick={() => setMethod('password')}
            >
              Password
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="confirm-identity-modal__form">
          {method === 'password' ? (
            <PasswordInput
              id="confirm-identity-password"
              label="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              disabled={verifyMutation.isPending}
            />
          ) : (
            <Field label="Authenticator code">
              <MfaCodeInput
                value={code}
                onChange={setCode}
                autoFocus
                disabled={verifyMutation.isPending}
              />
            </Field>
          )}

          {error && <p className="confirm-identity-modal__error">{error}</p>}

          <div className="confirm-identity-modal__actions">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={verifyMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={verifyMutation.isPending}>
              Confirm
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
